import { SkuSearchResultsDocumentTypes } from '@common/types/search';
import type { SkuSearchResultsTransformed } from '@common/types/search';
import { VictronImage } from '@components/display/images';
import { SearchResultsSection } from '@components/display/search';
import { SkuSearchResultsDocumentTypeConfigs } from '@constants/search';
import { useSearchContext } from '@contexts/SearchContext';
import { Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

export type SkuSearchResultsProps = { productData: SkuSearchResultsTransformed; loading: boolean };

export const SkuSearchResults = ({ productData, loading }: SkuSearchResultsProps) => {
    const { search } = useSearchContext();
    const intl = useIntl();

    return (
        <Stack gap="16px">
            {loading ? (
                <Stack
                    flexDirection="row"
                    alignItems="center"
                    gap="16px"
                    borderRadius="4px"
                    className="black-border"
                    p="10px"
                    bgcolor="secondary-alt.light"
                >
                    <Skeleton variant="rectangular" width={90} height={90} />
                    <Stack gap="8px">
                        <Stack flexDirection="row" alignItems="center" gap="12px">
                            <Skeleton variant="text" width={200} height={30} />
                            <Skeleton variant="rounded" width={60} height={24} />
                        </Stack>
                        <Skeleton variant="text" width={120} />
                    </Stack>
                </Stack>
            ) : (
                <Stack
                    flexDirection="row"
                    alignItems="center"
                    gap="16px"
                    borderRadius="4px"
                    className="black-border"
                    p="10px"
                    bgcolor="secondary-alt.light"
                >
                    <VictronImage src={productData.image} alt={productData.name} width="90" />
                    <Stack gap="8px">
                        <Stack flexDirection="row" alignItems="center" gap="12px">
                            <Typography fontSize={20} fontWeight={700} letterSpacing={-0.449} className="line-clamp-2">
                                {productData.name}
                            </Typography>
                            <Chip label={productData.sku_information[0].sku} color="primary" size="small" />
                        </Stack>
                        <Typography color="#908F8D" letterSpacing={-0.312} className="line-clamp-1">
                            {productData.category}
                        </Typography>
                    </Stack>
                </Stack>
            )}
            {SkuSearchResultsDocumentTypeConfigs.map(({ type, icon, name }) => (
                <SearchResultsSection
                    Icon={icon}
                    name={intl.formatMessage(name)}
                    results={productData.documents[type] ?? []}
                    search={search}
                    loading={loading}
                    totalResultsCount={productData.documents[type]?.length ?? 0}
                    downloadable
                />
            ))}
        </Stack>
    );
};
